import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useMutationCreateChannel from '@/modules/manage-player/api/hooks/useMutationCreateChannel'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useQueryGetDepositPromotionNamesForSelectorChannel from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionNamesForSelectorChannel'
import { Checkbox } from '@/common/ui/checkbox'

const INVALID_NAME_CHARS = /[&=?#]/

const formSchema = z.object({
	name: z
		.string()
		.min(1, 'กรุณากรอกชื่อรายการ')
		.max(100, 'ชื่อรายการต้องไม่เกิน 100 ตัวอักษร')
		.refine((v) => !INVALID_NAME_CHARS.test(v), {
			message: 'ชื่อรายการต้องไม่มีอักขระพิเศษ & = ? #',
		}),
	cost: z.number().min(0, 'ต้นทุนต้องมากกว่าหรือเท่ากับ 0').optional(),
	note: z.string().optional(),
	isActive: z.boolean().default(true),
	isGuest: z.boolean().default(false),
	promotionId: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

export default function CreateChannelFormModal() {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()
	const { data: promotions } =
		useQueryGetDepositPromotionNamesForSelectorChannel()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: '',
			note: '',
		},
	})
	const { mutate: createChannel } = useMutationCreateChannel()

	const handleSubmit = (values: FormValues) => {
		try {
			createChannel(
				{
					name: values.name.trim(),
					isActive: values.isActive,
					cost: values.cost,
					note: values.note?.trim(),
					isGuest: values.isGuest,
					promotionId: values.promotionId,
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetChannelList],
						})
						toast.success('เพิ่มช่องทางที่รู้จักเรียบร้อยแล้ว')
						setOpen(false)
						form.reset()
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<Button onClick={() => setOpen(true)} variant="default">
				<Plus className="mr-2 h-4 w-4" />
				เพิ่มช่องทางที่รู้จัก
			</Button>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>เพิ่มช่องทางที่รู้จัก</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-4"
					>
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ชื่อรายการ</FormLabel>
									<FormControl>
										<Input placeholder="กรอกชื่อรายการ" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="cost"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ต้นทุน</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="กรอกต้นทุน"
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : undefined,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="note"
							render={({ field }) => (
								<FormItem>
									<FormLabel>หมายเหตุ</FormLabel>
									<FormControl>
										<Input placeholder="กรอกหมายเหตุ (ถ้ามี)" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isGuest"
							render={({ field }) => (
								<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
									<div className="space-y-1 leading-none">
										<FormLabel>สำหรับผู้เล่นทดลอง</FormLabel>
										<p className="text-sm text-muted-foreground">
											เลือกว่าช่องทางนี้สำหรับผู้เล่นทดลองหรือไม่
										</p>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="promotionId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>โปรโมชั่น</FormLabel>
									<Select onValueChange={field.onChange} value={field.value}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกโปรโมชั่น" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{promotions?.data.map((promotion) => (
												<SelectItem key={promotion.id} value={promotion.id}>
													{promotion.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="flex justify-end gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => setOpen(false)}
							>
								ยกเลิก
							</Button>
							<Button type="submit">บันทึก</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
